import { useCallback } from 'react'

/**
 * Custom hook to encapsulate the filtering logic for pauta (checklist) items.
 *
 * @param {object} filterData - An object containing various filter flags (e.g., todo, backlogList, sinAccion, ejecutado, sinEjecutar).
 * @returns {(item: object) => boolean} A memoized function that returns `true` if an item passes the current filters, `false` otherwise.
 */
export const usePautaFiltering = (filterData) => {
    const filterItem = useCallback((item) => {
        if (!item) return false

        // 1. Global Override
        if (filterData.todo) {
            return true
        }

        // 2. Backlog Filter (Strict)
        // If backlogList is enabled, only show items that HAVE a backlog.
        if (filterData.backlogList) {
            if (!item.backlogList || item.backlogList.length === 0) {
                return false
            }
        }

        // 3. Status Filtering
        const isPurePending = !item.isChecked && !item.isWarning
        const isExecutedOK = item.isChecked === true && item.isWarning === false
        const isUnexecuted = item.isChecked === true && item.isWarning === true

        // Check against active filters
        // "Sin acciones" -> Purely pending (nothing marked)
        if (filterData.sinAccion && isPurePending) return true

        // "Sin ejecutar" -> Marked as unexecuted (isChecked && isWarning)
        if (filterData.sinEjecutar && isUnexecuted) return true

        // "Ejecutados" -> Checked and OK
        if (filterData.ejecutado && isExecutedOK) return true

        // If specific backlog filter is ON and no status filter matched,
        // we might still want to show it if no specific status was requested?
        // But usually filters are additive. If I select "Backlog" + "Ejecutado", I want backlogs that are executed.
        // If I select ONLY "Backlog", 'sinAccion', 'sinEjecutar', 'ejecutado' might be false?
        // In the UI, 'todo' usually handles the "all" case. If 'todo' is false, some other flags must be true.
        // If all status flags are false, nothing shows.

        return false
    }, [filterData])

    return filterItem
}
